import { CombinedGraphQLErrors, ErrorLike } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import * as Sentry from '@sentry/react';
import { isErrorLike } from '@apollo/client/errors';

export function getGraphQLErrors(error: unknown): Readonly<GraphQLFormattedError[]> {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors;
  }

  return [];
}

export function getGraphqlErrMessage({ t, error }: any) {
  const firstGraphQLError = findFirstGraphQLError(error);
  const code = firstGraphQLError?.extensions?.code;
  const errorMessage = error?.message || 'error';

  return code ? t([`ERRORS:${code}`, errorMessage]) : errorMessage;
}

type ErrorConditions = {
  codes?: string[];
  messages?: string[];
  errorNumbers?: string[];
  predicate?: (error: GraphQLFormattedError) => boolean;
};

/**
 * Given an CombinedGraphQLErrors object, find a graphQLError by message or code
 * @param error
 * @param conditions
 * @returns first match or undefined
 */
export function findGraphQLError(
  error: unknown,
  { codes, messages, errorNumbers, predicate }: ErrorConditions,
): GraphQLFormattedError | undefined {
  const graphQLErrors = getGraphQLErrors(error);

  return graphQLErrors.find(
    (graphQLError) =>
      codes?.includes(graphQLError.extensions?.code as string) ||
      messages?.includes(graphQLError.message) ||
      errorNumbers?.includes(graphQLError.extensions?.errorNumber as string) ||
      predicate?.(graphQLError),
  );
}

/**
 * Report unexpected errors to Sentry
 * @param error
 * @param variables
 */
export function reportGraphQLError(error: unknown, variables: any) {
  const operationalError = findGraphQLError(error, {
    codes: ['INSUFFICIENT_PERMISSIONS_ERROR', 'FORBIDDEN', 'INVALID_DLOCAL_PAYMENT_METHOD'],
  });
  const internalError = findGraphQLError(error, { codes: ['INTERNAL_SERVER_ERROR'] });

  if (!operationalError && !internalError) {
    Sentry.addBreadcrumb(variables);
    Sentry.captureException(error);
  }
}

export function isNetworkError(error: unknown): error is ErrorLike {
  return isErrorLike(error) && !CombinedGraphQLErrors.is(error);
}

export function findFirstGraphQLError(error: unknown) {
  return findGraphQLError(error, { predicate: () => true });
}
